import { useContext, useMemo } from 'react';
import {
  DRIVETRAIN_SECTION_ID,
  ENGINE_SECTION_ID,
  EXTERIOR_COLOR_SECTION_ID,
  INTERIOR_COLOR_SECTION_ID,
  PACKAGES_SECTION_ID
} from '~/constants/new-cars/sectionIds';
import SectionName from '~/constants/new-cars/SectionName';
import ParentModelContext from '~/context/new-cars/ParentModelContext';
import toLowerKebabCase from '~/utilities/toLowerKebabCase';
import useActiveOptionsSectionId from './useActiveOptionsSectionId';

export type NavItemStatus = 'active' | 'disabled' | 'link';

export type NavItem = {
  href?: string;
  isCompleted: boolean;
  isVisible: boolean;
  label: string;
  onClick?: () => void;
  status: NavItemStatus;
};

export default function useConfiguratorNavItems(): NavItem[] {
  const {
    activeSection,
    clearSelection,
    customizeOptions,
    isSectionIncluded,
    selectedOptions,
    selectedParentModel,
    sectionSequence
  } = useContext(ParentModelContext);

  const activeSectionIndex = sectionSequence.indexOf(activeSection);
  const isOnOptionsPage = activeSection === SectionName.Options;
  const showEngine = customizeOptions.data.engines.length > 0;

  const optionsSectionIds = useMemo(
    () => [
      EXTERIOR_COLOR_SECTION_ID,
      INTERIOR_COLOR_SECTION_ID,
      DRIVETRAIN_SECTION_ID,
      ...(showEngine ? [ENGINE_SECTION_ID] : []),
      PACKAGES_SECTION_ID
    ],
    [showEngine]
  );

  const activeOptionsSectionId = useActiveOptionsSectionId(
    optionsSectionIds,
    isOnOptionsPage
  );

  const { make } = selectedParentModel;
  const makeHref = `/new/${toLowerKebabCase(make)}`;

  return useMemo<NavItem[]>(() => {
    function getSequencedSectionStatus(section: SectionName): NavItemStatus {
      if (section === activeSection) return 'active';
      const sectionIndex = sectionSequence.indexOf(section);

      if (sectionIndex !== -1 && sectionIndex < activeSectionIndex)
        return 'link';

      return 'disabled';
    }

    function isSequencedSectionCompleted(section: SectionName): boolean {
      const sectionIndex = sectionSequence.indexOf(section);

      return sectionIndex !== -1 && sectionIndex < activeSectionIndex;
    }

    // Doors and ModelVersion are both pre-Trim steps represented by the single Model nav item.
    function getModelStatus(): NavItemStatus {
      if (
        activeSection === SectionName.Doors ||
        activeSection === SectionName.ModelVersion
      ) {
        return 'active';
      }

      if (
        isSequencedSectionCompleted(SectionName.Doors) ||
        isSequencedSectionCompleted(SectionName.ModelVersion)
      ) {
        return 'link';
      }

      return 'disabled';
    }

    function getOptionsSectionStatus(sectionId: string): NavItemStatus {
      if (isOnOptionsPage && activeOptionsSectionId === sectionId)
        return 'active';
      if (isOnOptionsPage) return 'link';

      return 'disabled';
    }

    const modelStatus = getModelStatus();
    const trimStatus = getSequencedSectionStatus(SectionName.Trim);
    const cabBoxStatus = getSequencedSectionStatus(SectionName.TruckBodyStyle);

    // Navigate back to the first section of the model selection phase
    const modelClearSection = isSectionIncluded(SectionName.Doors)
      ? SectionName.Doors
      : SectionName.ModelVersion;

    return [
      {
        href: makeHref,
        isCompleted: true,
        isVisible: true,
        label: make,
        status: 'link'
      },
      ...(isSectionIncluded(SectionName.Year)
        ? [
            {
              isCompleted: isSequencedSectionCompleted(SectionName.Year),
              isVisible: true,
              label: 'Year',
              onClick:
                getSequencedSectionStatus(SectionName.Year) === 'link'
                  ? () => clearSelection(SectionName.Year)
                  : undefined,
              status: getSequencedSectionStatus(SectionName.Year)
            }
          ]
        : []),
      ...(isSectionIncluded(SectionName.Doors) ||
      isSectionIncluded(SectionName.ModelVersion)
        ? [
            {
              isCompleted:
                isSequencedSectionCompleted(SectionName.Doors) ||
                isSequencedSectionCompleted(SectionName.ModelVersion),
              isVisible: true,
              label: 'Model',
              onClick:
                modelStatus === 'link'
                  ? () => clearSelection(modelClearSection)
                  : undefined,
              status: modelStatus
            }
          ]
        : []),
      {
        isCompleted: isSequencedSectionCompleted(SectionName.Trim),
        isVisible: true,
        label: 'Trim',
        onClick:
          trimStatus === 'link'
            ? () => clearSelection(SectionName.Trim)
            : undefined,
        status: trimStatus
      },
      {
        isCompleted: isSequencedSectionCompleted(SectionName.TruckBodyStyle),
        isVisible: isSectionIncluded(SectionName.TruckBodyStyle),
        label: 'Cab/Box',
        onClick:
          cabBoxStatus === 'link'
            ? () => clearSelection(SectionName.TruckBodyStyle)
            : undefined,
        status: cabBoxStatus
      },
      {
        href: `#${EXTERIOR_COLOR_SECTION_ID}`,
        isCompleted: !!selectedOptions?.exteriorColorCode,
        isVisible: true,
        label: 'Exterior Color',
        status: getOptionsSectionStatus(EXTERIOR_COLOR_SECTION_ID)
      },
      {
        href: `#${INTERIOR_COLOR_SECTION_ID}`,
        isCompleted: !!selectedOptions?.interiorColorCode,
        isVisible: true,
        label: 'Interior Color',
        status: getOptionsSectionStatus(INTERIOR_COLOR_SECTION_ID)
      },
      {
        href: `#${DRIVETRAIN_SECTION_ID}`,
        isCompleted: !!selectedOptions?.driveType,
        isVisible: true,
        label: 'Drivetrain',
        status: getOptionsSectionStatus(DRIVETRAIN_SECTION_ID)
      },
      {
        href: `#${ENGINE_SECTION_ID}`,
        isCompleted: !!selectedOptions?.engineCode,
        isVisible: showEngine,
        label: 'Engine',
        status: getOptionsSectionStatus(ENGINE_SECTION_ID)
      },
      {
        href: `#${PACKAGES_SECTION_ID}`,
        isCompleted: (selectedOptions?.packageOptionCodes?.length ?? 0) > 0,
        isVisible: true,
        label: 'Packages & Options',
        status: getOptionsSectionStatus(PACKAGES_SECTION_ID)
      }
    ];
  }, [
    activeOptionsSectionId,
    activeSection,
    activeSectionIndex,
    clearSelection,
    isSectionIncluded,
    isOnOptionsPage,
    make,
    makeHref,
    selectedOptions,
    sectionSequence,
    showEngine
  ]);
}
